import React, { useCallback, useMemo } from "react";
import { colors } from "../../../../utils/design";
import { correctStatus } from "../../../../utils/correctOrderStatus";
import Modal from "react-modal";
import { IoCloseSharp } from "react-icons/io5";
import { correctDate } from "../../../../utils/formatCreatedAt";
import maskMoney from "../../../../utils/maskMoney";
import { STATUS_COLORS } from "../orderCard";

export const ModalOrder = ({ close, data, open }) => {
  const parseFloat = useCallback((value) => Number.parseFloat(`${value}`), []);

  const summary = useMemo(() => {
    const subtotal = parseFloat(data.order_value);
    const delivery = parseFloat(data.delivery_value) * -1;
    const discount = parseFloat(data.discount_value);

    return {
      subtotal,
      delivery,
      discount,
      total: subtotal + delivery - discount,
    };
  }, [data, parseFloat]);

  const renderSummaryLine = useCallback(
    (label, value, colors = false, key = undefined) => {
      value = parseFloat(value);
      let color = "text-gray-800";

      if (colors && value !== 0) {
        color = value < 0 ? "text-green-500" : "text-red-500";
      }

      return (
        <div className="flex justify-between gap-2" key={key}>
          <span className="text-md">{label}:</span>
          {(value > 0 || (value === 0 && !colors)) && (
            <span className={`text-md font-bold ${color}`}>
              {maskMoney(Math.abs(value))}
            </span>
          )}
        </div>
      );
    },
    [parseFloat],
  );

  return (
    <Modal
      onRequestClose={close}
      isOpen={open}
      arialHideApp={false}
      style={{ overlay: { zIndex: 1000 } }}
      className="max-w-xl w-[95%] bg-white border-2 border-gray-200 rounded-lg fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 shadow-lg"
    >
      <div>
        <div className="flex flex-row items-center justify-between px-4 py-2 border-b-2 border-gray-200">
          <h2 className="font-bold text-lg">Ver pedido</h2>
          <button
            onClick={() => close()}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <IoCloseSharp size={30} color={colors.pinkRed} />
          </button>
        </div>

        <div className="max-h-[70dvh] overflow-auto p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div>
              <h4 className="text-sm leading-6">STATUS</h4>

              <span
                className={`px-3 py-1 text-sm rounded-full text-white ${STATUS_COLORS[data.status]}`}
              >
                {correctStatus[data.status]}
              </span>
            </div>

            <div>
              <h4 className="text-sm leading-6">Nº DO PEDIDO</h4>
              <p className="text-primary-500 font-bold break-words">
                {data.id}
              </p>
            </div>

            <div>
              <h4 className="text-sm leading-6">ESTABELECIMENTO</h4>
              <p className="text-primary-500 font-bold break-words">
                {data.restaurant?.fantasy_name}
              </p>
            </div>

            <div>
              <h4 className="text-sm leading-6">DATA</h4>
              <p className="text-primary-500 font-bold break-words">
                {correctDate(data.createdAt)}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-300">
            <h4 className="text-sm font-bold break-words border-b border-gray-300 px-4 py-3">
              CARRINHO
            </h4>

            <div>
              {data.items.map((item, indexItem) => (
                <div
                  key={`${item.product_id}-${indexItem}`}
                  className="select-none flex flex-col border-b border-gray-400 p-4 hover:bg-gray-200/75 transition-colors"
                >
                  <div className="flex flex-row justify-between">
                    <p className="text-primary-500 text-md mr-2">{item.qnt}x</p>
                    <div className="flex-grow">
                      {renderSummaryLine(item.product_name, item.product_price)}

                      {item.options.map((option, indexOption) =>
                        renderSummaryLine(
                          option.required_option_name,
                          option.required_option_price,
                          true,
                          `${option.required_option_id}-${indexItem}-${indexOption}`,
                        ),
                      )}

                      {item.additionals.map((additional, indexAdditional) =>
                        renderSummaryLine(
                          additional.additional_name,
                          additional.additional_price,
                          true,
                          `${additional.additional_id}-${indexItem}-${indexAdditional}`,
                        ),
                      )}

                      {item.product_price !== item.item_price &&
                        renderSummaryLine(
                          item.qnt === 1
                            ? "Subtotal"
                            : `Subtotal (${item.qnt}x)`,
                          item.item_price * item.qnt,
                        )}

                      {!!item.comments && (
                        <div className="border-l-4 border-secondary-500 bg-secondary-100/50 rounded-lg p-3 mt-2">
                          <p className="text-xs leading-tight font-bold">
                            OBSERVAÇÃO:
                          </p>
                          <p className="text-sm">
                            {item.comments}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4">
              {renderSummaryLine("Total", summary.subtotal)}
              {renderSummaryLine("Entrega", summary.delivery, true)}
              {renderSummaryLine("Desconto", summary.discount, true)}
              {renderSummaryLine("A pagar", summary.total)}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
